import type Channel from '@/Enums/Channel.enum';
import type { IDeviceControlOption } from '@/Interfaces/Device.interface';
import type { IImpressionCalculation } from '@/Interfaces/Forecaster';
import type { IFencingLocation, ITargetingLocation } from '@/Interfaces/Location.interface';
import AudienceTargetingService from '@/Services/AudienceTargeting.service';
import { getControlOptionsValues } from '@/helpers/filters/filterMapping';
import { type GetForecastForChannelParams, getForecastForChannel } from '@/store/api/forecaster.api';
import debounce from 'debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TabName } from '../TabName.enum';
import { DEFAULT_DAYS_IN_RANGE, TAB_DAYS_RANGE } from '../constants';

interface IProps {
  channel: Channel;
  channelId: number | null;
  geo?: { included?: ITargetingLocation[]; excluded?: ITargetingLocation[] };
  geoFence?: IFencingLocation | null;
  audience?: any;
  productId?: number;
  dealGroupIds?: number[];
  packageId?: number;
  devices?: IDeviceControlOption[];
  isNewLineItem: boolean;
  initialForecast: IImpressionCalculation | null;
  forceInitialFetch?: boolean;
}

const DEFAULT_DEAL_GROUP_IDS: number[] = [];
const DEFAULT_DEVICES: IDeviceControlOption[] = [];

export const useForecaster = ({
  channel,
  channelId,
  geo,
  geoFence,
  audience,
  productId,
  dealGroupIds = DEFAULT_DEAL_GROUP_IDS,
  packageId,
  devices = DEFAULT_DEVICES,
  isNewLineItem,
  initialForecast,
  forceInitialFetch = false,
}: IProps) => {
  const [activeTab, setActiveTab] = useState(TabName.WEEKLY);
  const [daysInRange, setDaysInRange] = useState(DEFAULT_DAYS_IN_RANGE);
  const [forecast, setForecast] = useState<IImpressionCalculation | null>(initialForecast);
  const [loading, setLoading] = useState(false);
  const isInitialMount = useRef(true);

  const forecastParams = useMemo(
    (): GetForecastForChannelParams => ({
      channelId: channelId ?? 0,
      geographic: geo?.included,
      geoExclusion: geo?.excluded,
      geoFence: geoFence || undefined,
      audience: AudienceTargetingService.getChannelAudienceTargeting(audience, channelId),
      productId,
      isCustomList: false,
      daysInRange: activeTab === TabName.CUSTOM ? daysInRange : TAB_DAYS_RANGE[activeTab],
      dealGroupIds,
      packageId,
      deviceIds: getControlOptionsValues(devices),
    }),
    [activeTab, daysInRange, channel, channelId, geo, geoFence, audience, productId, dealGroupIds, packageId, devices],
  );

  const fetchForecast = useCallback(async () => {
    setLoading(true);
    try {
      const forecast = await getForecastForChannel(forecastParams);
      setForecast(forecast);
    } finally {
      setLoading(false);
    }
  }, [forecastParams]);

  const fetchForecastDebounced = useMemo(() => debounce(fetchForecast, 1000), [fetchForecast]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (!isNewLineItem && !forceInitialFetch) {
        return;
      }
      fetchForecast();
      return;
    }

    fetchForecastDebounced();

    return () => {
      fetchForecastDebounced.clear();
    };
  }, [isNewLineItem, fetchForecast, fetchForecastDebounced]);

  return {
    activeTab,
    setActiveTab,
    daysInRange,
    setDaysInRange,
    forecast,
    loading,
  };
};
